import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import type { PieValueType } from "@mui/x-charts/models";
import { useTranslation } from "react-i18next";
import {
  taskStatusColors,
  meetingStatusColors,
  opportunityStageColors,
  TaskStatusKey,
  MeetingStatusKey,
  OpportunityStageKey,
} from "../utils/statusColors";
import { getMetadata, Metadata } from "../services/metadataService";
import { getTasks } from "../services/taskService";
import { getMeeting } from "../services/meetingService";
import { getAllOpportunities } from "../services/opportunityService";
import { Task, Meeting, Opportunity } from "../types";
import { getFromCache } from "../storage/cacheManager";

interface StatusData {
  id: number;
  value: number;
  label: string;
  color: string;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [taskStatusData, setTaskStatusData] = useState<StatusData[]>([]);
  const [meetingStatusData, setMeetingStatusData] = useState<StatusData[]>([]);
  const [opportunityStageData, setOpportunityStageData] = useState<
    StatusData[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const chartWidth = isMobile ? 320 : 420;
  const chartHeight = 300;
  const innerRadius = 60;
  const outerRadius = 100;

  const totalTasks = taskStatusData.reduce((sum, item) => sum + item.value, 0);
  const totalOpportunities = opportunityStageData.reduce(
    (sum, item) => sum + item.value,
    0,
  );

  const loadData = useCallback(async () => {
      setError(null);

      let metadata: Metadata | null = null;
      let tasks: Task[] = [];
      let meetings: Meeting[] = [];
      let opportunities: Opportunity[] = [];

      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user?.id;
        if (!userId) {
          setError(t("dashboard.noUserId", "Пользователь не авторизован"));
          setIsLoading(false);
          return;
        }

        try {
          if (!navigator.onLine) {
            const cachedMetadata = await getFromCache<Metadata>("metadata:app");
            const cachedTasks = await getFromCache<any>("tasks:list");
            const cachedMeetings = await getFromCache<any>("meetings:list");
            const cachedOpportunities =
              await getFromCache<any>("opportunities:list");

            const cachedTaskList = Array.isArray(cachedTasks)
              ? cachedTasks
              : cachedTasks?.list || [];
            const cachedMeetingList = Array.isArray(cachedMeetings)
              ? cachedMeetings
              : cachedMeetings?.list || [];
            const cachedOpportunityList = Array.isArray(cachedOpportunities)
              ? cachedOpportunities
              : cachedOpportunities?.list || [];

            if (cachedMetadata) metadata = cachedMetadata;
            if (cachedTaskList) tasks = cachedTaskList;
            if (cachedMeetingList) meetings = cachedMeetingList;
            if (cachedOpportunityList) opportunities = cachedOpportunityList;
          } else {
            const [
              metadataResult,
              taskResponse,
              meetingResponse,
              opportunityResponse,
            ] = await Promise.allSettled([
              getMetadata(),
              getTasks(userId),
              getMeeting(userId),
              getAllOpportunities(),
            ]);

            if (metadataResult.status === "fulfilled") {
              metadata = metadataResult.value;
            } else {
              const cached = await getFromCache<Metadata>("metadata:app");
              if (cached) metadata = cached;
            }

            if (taskResponse.status === "fulfilled") {
              const taskData = taskResponse.value;
              tasks = Array.isArray(taskData) ? taskData : taskData?.list || [];
            } else {
              const cached = await getFromCache<Task[]>("tasks:list");
              if (cached) tasks = cached;
            }

            if (meetingResponse.status === "fulfilled") {
              const meetingData = meetingResponse.value;
              meetings = Array.isArray(meetingData)
                ? meetingData
                : meetingData?.list || [];
            } else {
              const cached = await getFromCache<Meeting[]>("meetings:list");
              if (cached) meetings = cached;
            }

            if (opportunityResponse.status === "fulfilled") {
              const oppData = opportunityResponse.value;
              opportunities = Array.isArray(oppData)
                ? oppData
                : oppData?.list || [];
            } else {
              const cached =
                await getFromCache<Opportunity[]>("opportunities:list");
              if (cached) opportunities = cached;
            }
          }
        } catch (fetchError) {
          console.error("Ошибка при загрузке данных:", fetchError);
          setError(t("dashboard.fetchError", "Не удалось загрузить данные"));
          setIsLoading(false);
          return;
        }

        if (!metadata) {
          setError(t("dashboard.noMetadata", "Метаданные не загружены"));
          setIsLoading(false);
          return;
        }

        const taskStatusCounts = tasks.reduce(
          (acc, task) => {
            acc[task.status] = (acc[task.status] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        );

        const meetingStatusCounts = meetings.reduce(
          (acc, meeting) => {
            acc[meeting.status] = (acc[meeting.status] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        );

        const opportunityStageCounts = opportunities.reduce(
          (acc, opp) => {
            acc[opp.stage] = (acc[opp.stage] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        );

        const taskStatusList = metadata.entityDefs?.Task?.statusList || [
          "Not Started",
          "Started",
          "Completed",
          "Deferred",
          "Canceled",
        ];
        const meetingStatusList = metadata.entityDefs?.Meeting?.statusList || [
          "Planned",
          "Held",
          "Not Held",
        ];
        const opportunityStageList = metadata.entityDefs?.Opportunity
          ?.stageList || [
          "Prospecting",
          "Qualification",
          "Negotiation",
          "Logistic",
          "Closed Won",
          "Closed Lost",
        ];

        const taskStatusData = taskStatusList
          .map((status: string, index: number) => {
            const translatedStatus = t(
              `statuses.task.${status.toLowerCase().replace(/\s/g, "_")}`,
              status,
            );
            return {
              id: index,
              value: taskStatusCounts[status] || 0,
              label: translatedStatus,
              color:
                status in taskStatusColors
                  ? taskStatusColors[status as TaskStatusKey].color
                  : "#ccc",
            };
          })
          .filter((data: StatusData) => data.value > 0);

        const meetingStatusData = meetingStatusList
          .map((status: string, index: number) => {
            const translatedStatus = t(
              `statuses.meeting.${status.toLowerCase().replace(/\s/g, "_")}`,
              status,
            );
            return {
              id: index,
              value: meetingStatusCounts[status] || 0,
              label: translatedStatus,
              color:
                status in meetingStatusColors
                  ? meetingStatusColors[status as MeetingStatusKey].color
                  : "#ccc",
            };
          })
          .filter((data: StatusData) => data.value > 0);

        const opportunityStageData = Object.keys(opportunityStageCounts)
          .map((stage: string, index: number) => {
            const translatedStage = t(
              `statuses.opportunity.${stage.toLowerCase().replace(/\s/g, "_")}`,
              stage,
            );
            return {
              id: index + 1000,
              value: opportunityStageCounts[stage] || 0,
              label: translatedStage,
              color:
                stage in opportunityStageColors
                  ? opportunityStageColors[stage as OpportunityStageKey].color
                  : "#ccc",
            };
          })
          .filter((data: StatusData) => data.value > 0);

        setTaskStatusData(taskStatusData);
        setMeetingStatusData(meetingStatusData);
        setOpportunityStageData(opportunityStageData);
      } catch (err) {
        console.error("Ошибка при обработке данных:", err);
        setError(t("dashboard.invalidData", "Ошибка при обработке данных"));
      } finally {
        setIsLoading(false);
      }
  }, [t]);

  useEffect(() => {
    loadData();

    const handleTasksChanged = () => {
      loadData();
    };
    window.addEventListener("tasks:changed", handleTasksChanged);
    return () => {
      window.removeEventListener("tasks:changed", handleTasksChanged);
    };
  }, [loadData]);

  if (isLoading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 2, pb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t("dashboard.title", "Dashboard")}
        </Typography>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 0, p: 0 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
      >
        {t("dashboard.title", "Dashboard")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
        }}
      >
        {/* График задач */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 49%" },
            borderRight: {
              xs: "none",
              sm: `1px solid ${theme.palette.divider}`,
            },
            pr: { xs: 0, sm: 1.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {t("dashboard.taskStatistics", "Статистика задач")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t("dashboard.total", "Всего")}:
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {totalTasks}
              </Typography>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : taskStatusData.length > 0 ? (
            <Box
              ref={(node: HTMLDivElement | null) => {
                if (node) {
                  const chartWrapper = node.querySelector(
                    "div > div",
                  ) as HTMLElement | null;
                  if (chartWrapper) {
                    chartWrapper.style.gridTemplateAreas = "unset";
                  }
                }
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                "& > div:first-of-type": {
                  gridTemplateAreas: "unset !important",
                },
              }}
            >
              <PieChart
                width={chartWidth}
                height={chartHeight}
                series={[
                  {
                    innerRadius: innerRadius,
                    outerRadius: outerRadius,
                    data: taskStatusData,
                    arcLabel: (item: PieValueType) => {
                      return item.value > 0 ? `${item.value}` : "";
                    },
                    arcLabelMinAngle: 15,
                    arcLabelRadius: 85,
                    paddingAngle: 4,
                    cornerRadius: 4,
                    highlightScope: { fade: "global", highlight: "item" },
                  },
                ]}
                slotProps={{
                  legend: {
                    direction: "horizontal",
                    position: { vertical: "bottom", horizontal: "center" },
                  },
                }}
                sx={{
                  "& .MuiChartsArcLabel": {
                    fill: "#fff !important",
                    fontWeight: 700,
                    fontSize: "14px !important",
                    filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))",
                    textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                  },
                  "& text": {
                    fill: "#fff !important",
                    fontWeight: 700,
                    fontSize: "14px !important",
                    filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))",
                  },
                }}
                margin={{ top: 8, bottom: 60, left: 8, right: 8 }}
              />
            </Box>
          ) : (
            <Typography color="textSecondary" sx={{ py: 2 }}>
              {t("dashboard.noData", "Нет данных")}
            </Typography>
          )}
        </Box>

        {/* График сделок */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 49%" },
            pl: { xs: 0, sm: 1.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {t("dashboard.opportunityStatistics", "Статистика сделок")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t("dashboard.total", "Всего")}:
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {totalOpportunities}
              </Typography>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : opportunityStageData.length > 0 ? (
            <Box
              ref={(node: HTMLDivElement | null) => {
                if (node) {
                  const chartWrapper = node.querySelector(
                    "div > div",
                  ) as HTMLElement | null;
                  if (chartWrapper) {
                    chartWrapper.style.gridTemplateAreas = "unset";
                  }
                }
              }}
              sx={{
                display: "flex",
                justifyContent: "center",
                "& > div:first-of-type": {
                  gridTemplateAreas: "unset !important",
                },
              }}
            >
              <PieChart
                width={chartWidth}
                height={chartHeight}
                series={[
                  {
                    innerRadius: innerRadius,
                    outerRadius: outerRadius,
                    data: opportunityStageData,
                    arcLabel: (item: PieValueType) => {
                      return item.value > 0 ? `${item.value}` : "";
                    },
                    arcLabelMinAngle: 15,
                    arcLabelRadius: 85,
                    paddingAngle: 4,
                    cornerRadius: 4,
                    highlightScope: { fade: "global", highlight: "item" },
                  },
                ]}
                slotProps={{
                  legend: {
                    direction: "horizontal",
                    position: { vertical: "bottom", horizontal: "center" },
                  },
                }}
                sx={{
                  "& .MuiChartsArcLabel": {
                    fill: "#fff !important",
                    fontWeight: 700,
                    fontSize: "14px !important",
                    filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))",
                    textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                  },
                  "& text": {
                    fill: "#fff !important",
                    fontWeight: 700,
                    fontSize: "14px !important",
                    filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.7))",
                  },
                }}
                margin={{ top: 8, bottom: 60, left: 8, right: 8 }}
              />
            </Box>
          ) : (
            <Typography color="textSecondary" sx={{ py: 2 }}>
              {t("dashboard.noData", "Нет данных")}
            </Typography>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Dashboard;
